import {Layers, RockBottomLayers, SimpleStageLayer} from "../../../view/RockBottomLayers";
import {Expo, TimelineLite, TweenLite} from "gsap";
import {RockBottom} from "../../../RockBottom";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {RockBottomParsedGameData} from "../../../server/data/RockBottomParsedGameData";
import {RockBottomGameAssets} from "../../../RockBottomGameAssets";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {RockBottomSymbol} from "../../../symbol/RockBottomSymbol";
import {IBetWinData} from "../../../server/data/IRockBottomData";
import {RockBottomGameConfig} from "../../../RockBottomGameConfig";
import {RockBottomGameMode} from "../../../server/RockBottomGameMode";
import {RockBottomSound} from "../../../sound/RockBottomSound";

export class InitialWinMultView extends PIXI.Container {

    private _winAmountForSym: BitmapCountUp[] = [];
    private _winMultForSym: PIXI.BitmapText[] = [];
    private _layer: SimpleStageLayer | undefined;
    private _flyerContainer: PIXI.Container;
    private _multPrefix: string = "x";
    private _fontStyle: any = {
        fontName: RockBottomGameAssets.BITMAP_FONT_WIN_COUNT_UP,
        fontSize: 12,
        letterSpacing: -15
    };
    private _flyDuration: number = 0.3;

    constructor() {
        super();
        this.init();
    }

    public updateProps(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            isBelowStakeWin: boolean = BelowStakeWinRestriction.isWinBelowPlayedBetValue(),
            parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData;

        if (RockBottom.inBonusGame && !isBelowStakeWin && parsedGameData.currentWinRatioConfig?.bigWinLevel === -1) {
            for (let i: number = 0; i < parsedGameData.betWins.length; i++) {
                const betWinData: IBetWinData = parsedGameData.betWins[i],
                    amountTxt: number = betWinData.amount,
                    multTxt: string = this._multPrefix + betWinData.multiplier,
                    winSymName: string = betWinData.symbol,
                    winAmountElement: BitmapCountUp = this._winAmountForSym[i],
                    winMultElement: PIXI.BitmapText = this._winMultForSym[i];

                tl.to(winAmountElement, {
                    alpha: 1,
                    duration: this._flyDuration,
                    onStart: () => {
                        winAmountElement.getCountUpAnimation(0.01, amountTxt, this._flyDuration, `power3.inOut`);
                        winMultElement.text = multTxt;
                        this.placeWinAmountAndMult(i, winSymName);
                        if (!RockBottomSound.soundPlayer.fetchSoundPlayer(RockBottomSound.BONUS_GAME_MULTIPLIER_AMOUNT)?.isPlaying) {
                            RockBottomSound.playTrack(RockBottomSound.BONUS_GAME_MULTIPLIER_AMOUNT);
                        }
                    }
                }, 0);
                tl.add(this.flyMultipliers(i, winSymName), 0.3);
            }
        }

        return tl;
    }

    public remove(): void {
        for (let i: number = 0; i < 2; i++) {
            TweenLite.to([this._winAmountForSym[i], this._winMultForSym[i], this._flyerContainer], {
                alpha: 0,
                duration: 0.1,
                onComplete: () => {
                    this._winAmountForSym[i].text = "";
                    this._winMultForSym[i].text = "";
                    this._flyerContainer.alpha = 1;
                    this._flyerContainer.removeChildren();
                }
            });
        }
    }

    private getSymbolData(symName: string): RockBottomSymbol {
        const parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData,
            evaluatedReels: string[][] = parsedGameData.evaluatedArea;

        let reelId: number;
        if (evaluatedReels[2].includes(symName)) {
            reelId = 2;
        } else if (evaluatedReels[1].includes(symName)) {
            reelId = 1;
        } else {
            reelId = 3;
        }

        return RockBottom.reels[reelId].getSymbol(evaluatedReels[reelId].indexOf(symName));
    }

    private placeWinAmountAndMult(betWinIdx: number, symName: string): void {
        const winSymbol: RockBottomSymbol = this.getSymbolData(symName),
            symPos: PIXI.Point = winSymbol.getGlobalPosition(),
            winMultElement: PIXI.BitmapText = this._winMultForSym[betWinIdx],
            winAmountElement: BitmapCountUp = this._winAmountForSym[betWinIdx];

        winAmountElement.pivot.set(winAmountElement.width / 2, winAmountElement.height / 2);
        winAmountElement.position.set(symPos.x, (symPos.y + (winSymbol.height / 2)) - 10);
        winMultElement.pivot.set(winMultElement.width / 2, winMultElement.height / 2);
        winMultElement.position.set(symPos.x, symPos.y + 40);
    }

    private flyMultipliers(betWinIdx: number, winSymName: string): TimelineLite {
        const parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData,
            winSymIdx: number = RockBottomGameConfig.MID_SYMS.indexOf(winSymName),
            winSymMultVal: number = parsedGameData.fsSymbolMultiplier[winSymIdx],
            flyingMultPos: PIXI.Point = this.getMultPos(winSymIdx),
            symMultiplierOnReel: PIXI.BitmapText = this._winMultForSym[betWinIdx],
            flyingSymMultiplier: PIXI.BitmapText = new PIXI.BitmapText(this._multPrefix + winSymMultVal, this._fontStyle),
            tl: TimelineLite = new TimelineLite();

        flyingSymMultiplier.alpha = 0;
        flyingSymMultiplier.position.set(flyingMultPos.x, flyingMultPos.y);
        this._flyerContainer.addChild(flyingSymMultiplier);

        tl.add(this.flyThis(flyingSymMultiplier, betWinIdx), 0);

        if (parsedGameData.mode === RockBottomGameMode.SUPER_FREESPIN) {
            const evaluatedReels: string[][] = parsedGameData.evaluatedArea,
                activeSymName: string = RockBottom.midSymExpand.getActiveSymbolName()!,
                hasActiveSym: boolean = evaluatedReels[1].includes(activeSymName) || evaluatedReels[2].includes(activeSymName) || evaluatedReels[3].includes(activeSymName);

            if (hasActiveSym) {
                const activeSymIdx: number = RockBottom.midSymExpand.getActiveSymbolId()!,
                    activeSymMultVal: number = parsedGameData.fsSymbolMultiplier[activeSymIdx],
                    flyingMultPosForActive: PIXI.Point = this.getMultPos(activeSymIdx),
                    flyingActiveSymMultiplier: PIXI.BitmapText = new PIXI.BitmapText(this._multPrefix + activeSymMultVal, this._fontStyle);

                flyingActiveSymMultiplier.alpha = 0;
                flyingActiveSymMultiplier.position.set(flyingMultPosForActive.x, flyingMultPosForActive.y);
                this._flyerContainer.addChild(flyingActiveSymMultiplier);

                tl.add(this.flyThis(flyingActiveSymMultiplier, betWinIdx), 0);
            }
        }
        return tl;
    }

    private flyThis(type: PIXI.BitmapText, betWinIdx: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData,
            betWinData: IBetWinData = parsedGameData.betWins[betWinIdx],
            winMultElement: PIXI.BitmapText = this._winMultForSym[betWinIdx],
            winAmountElement: BitmapCountUp = this._winAmountForSym[betWinIdx],
            winSymbol: RockBottomSymbol = this.getSymbolData(betWinData.symbol),
            symPos: PIXI.Point = winSymbol.getGlobalPosition();

        tl.add(TweenLite.to(type, {
            x: symPos.x -20,
            y: symPos.y + 22,
            duration: this._flyDuration,
            ease: Expo.easeInOut,
            onStart: () => {
                type.alpha = 1;
            },
            onComplete: () => {
                type.alpha = 0;
                winMultElement.alpha = 1;
            }
        }), 0);
        tl.to(this, {duration: this._flyDuration}, tl.duration());
        tl.add(TweenLite.to(winMultElement, {
            y: symPos.y + 12,
            duration: this._flyDuration / 2
        }), tl.duration());
        tl.to(this, {duration: this._flyDuration}, tl.duration());
        tl.add([
            TweenLite.to(winMultElement, {
                y: symPos.y + 30,
                duration: this._flyDuration / 3
            }),
            TweenLite.to([winMultElement], {
                alpha: 0,
                duration: this._flyDuration / 3
            }), TweenLite.to([winAmountElement], {
                alpha: 0.05,
                duration: this._flyDuration / 3,
                onComplete: () => {
                    winAmountElement.alpha = 1;
                    winAmountElement.text = (betWinData.multipliedAmount).toFixed(2) + "";
                }
            })
        ], tl.duration());
        tl.add([
            TweenLite.fromTo(winAmountElement.scale, {
                x: 1,
                y: 1
            }, {
                x: 1.2,
                y: 1.2,
                duration: this._flyDuration * 2,
                ease: `elastic.out(1, 0.3)`,
                onStart: () => {
                    const prevPivot: PIXI.ObservablePoint = winAmountElement.pivot;
                    winAmountElement.pivot.set(winAmountElement.width / 2, prevPivot.y);
                }
            }),
            TweenLite.to(winAmountElement, {
                alpha: 0,
                duration: this._flyDuration
            })
        ], tl.duration());
        //tl.to(this, {duration: this._flyDuration}, tl.duration());
        /*tl.add(TweenLite.to(winAmountElement, {
            alpha: 0,
            duration: this._flyDuration / 3
        }), tl.duration());*/
        tl.to(this, {duration: this._flyDuration}, tl.duration());

        return tl;
    }

    private getMultPos(symIdx: number): PIXI.Point {
        let multPos: PIXI.Point;
        if (symIdx === 0) {
            multPos = new PIXI.Point(658, 305);
        } else if (symIdx === 1) {
            multPos = new PIXI.Point(653, 510);
        } else if (symIdx === 2) {
            multPos = new PIXI.Point(80, 260);
        } else if (symIdx === 3) {
            multPos = new PIXI.Point(80, 410);
        } else {
            multPos = new PIXI.Point(85, 550);
        }
        return multPos;
    }

    private init(): void {
        this._layer = RockBottomLayers.getLayer(Layers.WIN_COUNT_UP);
        this.createFlyerContainer();
        this.createMult();
        this.createAmount();

        this.position.set(0, -20);

        this._layer?.addChildAt(this, 0);
    }

    private createFlyerContainer(): void {
        this._flyerContainer = new PIXI.Container();
        this.addChild(this._flyerContainer);
    }

    private createAmount(): void {
        for (let i: number = 0; i < 2; i++) {
            const amountObj: BitmapCountUp = new BitmapCountUp(this._fontStyle);
            amountObj.shrinkSize = true;
            amountObj.alpha = 0;
            this._winAmountForSym.push(amountObj);
            this.addChild(this._winAmountForSym[i]);
        }
    }

    private createMult(): void {
        for (let i: number = 0; i < 2; i++) {
            const multObj: PIXI.BitmapText = new PIXI.BitmapText("", this._fontStyle);
            multObj.alpha = 0;
            this._winMultForSym.push(multObj);
            this.addChild(this._winMultForSym[i]);
        }
    }
}